import * as React from 'react';

import isEmpty from 'lodash/isEmpty';
import { isJcsCreatePasswordForNewUserEnabled, isJsmPortalRovoIntegrationEnabled } from '@helpcenter/feature-flags';
import type { MessageDescriptor } from 'react-intl-next';
import { LoginAttemptErrorType } from 'rest/login/login';
import type { changeEmailInput, checkLoginAction, forgotPassword, loginAttempt, resetLogin } from 'state/actions/login';
import type { LoginUIState } from 'state/ui/login';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { focus } from '@atlassian/help-center-common-util/dom';
import { getBaseName } from '@atlassian/help-center-common-util/history';
import { search } from '@atlassian/help-center-common-util/location';
import { parseLoginSignupRedirectUrlParams } from '@atlassian/help-center-common-util/url';
import { getSsoTestModeParamFromUrl } from '@atlassian/help-center-common-util/url/url';
import type { createPassword } from '../../../state/actions/login/login';
import { LoginFormStateless } from '../login-form-stateless';
import messages from './messages';

interface LoginFormProps {
    contactAdminLink: string;
    resetUrl: string;
    loginUIState: LoginUIState;
    canResetPassword: boolean;
    // injected via hocs
    changeEmailInputAction: typeof changeEmailInput;
    checkLoginActionAction: typeof checkLoginAction;
    loginAttemptAction: typeof loginAttempt;
    forgotPasswordAction: typeof forgotPassword;
    createPasswordAction: typeof createPassword;
    resetLoginAction: typeof resetLogin;
    loginAnnouncementLocale?: string;
    portalId: string | undefined;

    continueParam: string;
}

interface LoginFormState {
    password: string;
    validEmail: boolean;
    isPasswordEmptyOnSubmit: boolean;
}

// eslint-disable-next-line no-useless-escape
const EMAIL_PATTERN = /.+\@.+\..+/;

export class LoginForm extends React.Component<LoginFormProps & WithAnalyticsEventsProps, LoginFormState> {
    state: LoginFormState = {
        password: '',
        validEmail: true,
        isPasswordEmptyOnSubmit: false,
    };

    onEmailFieldChange = (email: string) => {
        this.props.changeEmailInputAction(email);
    };

    onEmailFieldFocus = () => {
        if (!this.props.loginUIState.email) {
            this.props.resetLoginAction(this.props.loginUIState.email);
        }
    };

    onPasswordChange = (password: string) => {
        this.setState({
            password,
        });
    };

    validateEmail = (): boolean => {
        const validEmail = EMAIL_PATTERN.test(this.props.loginUIState.email);
        this.setState({
            validEmail,
        });
        return validEmail;
    };

    onSubmit = () => {
        if (this.validateEmail()) {
            const { loginUIState, loginAttemptAction, checkLoginActionAction, createAnalyticsEvent, continueParam } =
                this.props;

            const authData = this.buildAuthDataRequest();
            const { password } = this.state;

            if (loginUIState.atlassianAccountCheckPerformed) {
                const isPasswordEmptyOnSubmit = isEmpty(password);
                this.setState({ isPasswordEmptyOnSubmit });

                if (!isPasswordEmptyOnSubmit) {
                    loginAttemptAction({ password, continueParam, email: loginUIState.email.trim() });
                }
            } else {
                checkLoginActionAction(loginUIState.email.trim(), this.props.portalId, authData, createAnalyticsEvent);
            }
        } else {
            focus('user-email');
        }
    };

    private buildAuthDataRequest() {
        const isInIframe = window.self !== window.top;
        const { destination, isAbsolute } = parseLoginSignupRedirectUrlParams();
        const prefix = destination && isAbsolute ? '' : `${getBaseName()}/`;
        const redirectUrl = `${prefix}${destination || this.getDefaultDestination()}`;
        const params = new URLSearchParams(search());

        return isInIframe && isJsmPortalRovoIntegrationEnabled()
            ? {
                  ssoTest: getSsoTestModeParamFromUrl(params),
                  redirectTo: '/servicedesk/customer/embeds/login-success',
                  absoluteRedirectUrl: false,
              }
            : {
                  ssoTest: getSsoTestModeParamFromUrl(params),
                  redirectTo: redirectUrl,
                  absoluteRedirectUrl: isAbsolute,
              };
    }

    getDefaultDestination = (): string => {
        const { portalId } = this.props;
        if (portalId) {
            return `portal/${portalId}`;
        }
        return 'portals';
    };

    onForgotPasswordLinkClick = () => {
        const { loginUIState, forgotPasswordAction, resetUrl } = this.props;
        forgotPasswordAction(resetUrl, loginUIState.email.trim());
    };

    onCreatePasswordLinkClick = () => {
        const { loginUIState, createPasswordAction, resetUrl } = this.props;
        createPasswordAction(resetUrl, loginUIState.email.trim());
    };

    getLoginError = (): MessageDescriptor | undefined => {
        const { error, loginAttemptResponse } = this.props.loginUIState;
        if (error) {
            return error.description;
        }

        const loginFailure = loginAttemptResponse?.error;
        if (loginFailure) {
            switch (loginFailure.errorType) {
                case LoginAttemptErrorType.INVALID_CREDENTIALS:
                    return messages.invalidLogin;
                case LoginAttemptErrorType.ACCOUNT_LOCKED_ERROR:
                    return messages.accountLocked;
                case LoginAttemptErrorType.MISC_LOGIN_ERROR:
                default:
                    return messages.miscLoginError;
            }
        }

        return undefined;
    };

    render() {
        const { canResetPassword, loginUIState, contactAdminLink, loginAnnouncementLocale } = this.props;
        const { validEmail, isPasswordEmptyOnSubmit, password } = this.state;

        return (
            <LoginFormStateless
                isSubmitting={loginUIState.isSubmitting}
                isSendingForgotPasswordEmail={loginUIState.isSendingForgotPasswordEmail}
                isSendingCreatePasswordEmail={loginUIState.isSendingCreatePasswordEmail}
                onSubmit={this.onSubmit}
                onEmailFieldChange={this.onEmailFieldChange}
                onPasswordChange={this.onPasswordChange}
                validEmail={validEmail}
                isPasswordEmptyOnSubmit={isPasswordEmptyOnSubmit}
                email={loginUIState.email}
                password={password}
                loginError={this.getLoginError()}
                atlassianAccountCheckPerformed={loginUIState.atlassianAccountCheckPerformed}
                loginFailure={loginUIState.loginAttemptResponse?.error}
                showLoginConsentError={loginUIState.showLoginConsentErrorPage}
                createAnalyticsEvent={this.props.createAnalyticsEvent}
                onEmailFieldFocus={this.onEmailFieldFocus}
                onForgotPasswordLinkClick={this.onForgotPasswordLinkClick}
                onCreatePasswordLinkClick={
                    isJcsCreatePasswordForNewUserEnabled() ? this.onCreatePasswordLinkClick : undefined
                }
                contactAdminLink={contactAdminLink}
                canResetPassword={canResetPassword}
                loginAnnouncementLocale={loginAnnouncementLocale}
            />
        );
    }
}
